/* eslint-disable no-unused-vars */
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { db } from '../../config/firebaseConfig';
import { transitionToWinnerDisplay } from '../../utils/astrapos/stageTransitions';
import {
  assignRolesAfterStaking,
  checkStakingEligibility,
  determineStakingWinner,
  placeStake,
  refundLosingStakes,
  updateStake,
} from '../../utils/astrapos/stakingLogic';
import GameCountdown from './GameCountdown';

/**
 * STAKING stage: Players stake coins to compete for Validator role
 * Real-time stakes leaderboard shows all players and their stakes
 */
const StakingStage = ({ roomId, roomData, userData, role }) => {
  const [stakeAmount, setStakeAmount] = useState('');
  const [hasStaked, setHasStaked] = useState(false);
  const [isStaking, setIsStaking] = useState(false);
  const [stakes, setStakes] = useState([]);
  const [allPlayers, setAllPlayers] = useState([]);
  const [transitionInProgress, setTransitionInProgress] = useState(false);
  const [userStakeId, setUserStakeId] = useState(null);
  const [currentUserStake, setCurrentUserStake] = useState(null);
  const [participantDetails, setParticipantDetails] = useState({});

  useEffect(() => {
    const fetchParticipantDetails = async () => {
      const allPks = [...(roomData.participants || []), ...(roomData.miners || [])];
      if (allPks.length === 0) return;

      try {
        const usersRef = db.collection('users');
        const querySnapshot = await usersRef.where('publicKey', 'in', allPks).get();
        const details = {};
        querySnapshot.forEach((doc) => {
          const userData = doc.data();
          details[userData.publicKey] = userData;
        });
        setParticipantDetails(details);
      } catch (error) {
        console.error("Error fetching participant details:", error);
      }
    };

    if (roomData.participants || roomData.miners) {
      fetchParticipantDetails();
    }
  }, [roomData.participants, roomData.miners]);

  const currentCycle = roomData.gameState?.currentCycle || 1;
  const { eligible, message } = checkStakingEligibility(userData);

  // Subscribe to real-time stakes
  useEffect(() => {
    const stakesQuery = query(
      collection(db, 'rooms', roomId, 'stakes'),
      where('gameCycle', '==', currentCycle)
    );

    const unsubscribe = onSnapshot(stakesQuery, (snapshot) => {
      const stakesData = [];
      snapshot.forEach((doc) => {
        stakesData.push({ id: doc.id, ...doc.data() });
      });

      // Sort: highest stake, then earliest time, then alphabetically
      const sorted = stakesData.sort((a, b) => {
        if (b.stakeAmount !== a.stakeAmount) {
          return b.stakeAmount - a.stakeAmount;
        }
        // Handle Firestore Timestamp objects
        const timeA =
          a.stakedAt?.toDate?.().getTime?.() || a.stakedAt?.getTime?.() || 0;
        const timeB =
          b.stakedAt?.toDate?.().getTime?.() || b.stakedAt?.getTime?.() || 0;
        if (timeA !== timeB) {
          return timeA - timeB;
        }
        return a.playerEmail.localeCompare(b.playerEmail);
      });

      setStakes(sorted);

      // Check if current user has staked
      const userStake = stakesData.find(
        (s) => s.playerPublicKey === userData.publicKey
      );
      setHasStaked(!!userStake);
      setUserStakeId(userStake?.id || null);
      setCurrentUserStake(userStake || null);
    });

    return () => unsubscribe();
  }, [roomId, currentCycle, userData.publicKey]);

  // Build all players list for display
  useEffect(() => {
    const participants = (roomData.participants || []).concat(
      roomData.miners || []
    );
    setAllPlayers(participants);
  }, [roomData.participants, roomData.miners]);

  // Handle staking expiry
  const handleStagingExpiry = async () => {
    if (transitionInProgress) return;
    setTransitionInProgress(true);

    try {
      // Determine winner
      const allPlayersEmails = await Promise.all(
        allPlayers.map(async (pk) => {
          const user = userData.publicKey === pk ? userData : null;
          if (user) return user.email;
          // For other players, we'd need to fetch from DB
          return null;
        })
      );

      const winner = await determineStakingWinner(
        roomId,
        currentCycle,
        allPlayers
      );

      // Refund losers
      await refundLosingStakes(roomId, winner.publicKey, currentCycle);

      // Assign roles
      await assignRolesAfterStaking(roomId, winner.publicKey, allPlayers);

      // Transition to winner display
      await transitionToWinnerDisplay(roomId, winner);

      toast.success(
        `Staking round complete! ${winner.email || 'A player'} won!`
      );
    } catch (error) {
      console.error('Error during staking expiry:', error);
      toast.error('Error completing staking round');
    }
  };

  const handlePlaceStake = async (e) => {
    e && e.preventDefault();

    if (!eligible) {
      toast.error(message);
      return;
    }

    if (!stakeAmount || parseFloat(stakeAmount) <= 0) {
      toast.error('Stake must be greater than 0');
      return;
    }

    const newStakeAmount = parseFloat(stakeAmount);

    // If user is updating an existing stake
    if (hasStaked && userStakeId && currentUserStake) {
      const stakeDifference = newStakeAmount - currentUserStake.stakeAmount;
      const maxAllowedStake = userData.balance + currentUserStake.stakeAmount;

      // Check if new stake exceeds max allowed
      if (newStakeAmount > maxAllowedStake) {
        toast.error(
          `Stake is larger than your balance. Maximum allowed: ${maxAllowedStake.toFixed(2)} coins`
        );
        return;
      }

      // If increasing the stake, check if user has enough balance
      if (stakeDifference > 0 && stakeDifference > userData.balance) {
        toast.error('Insufficient balance to increase stake');
        return;
      }

      setIsStaking(true);
      try {
        await updateStake(
          roomId,
          userData,
          newStakeAmount,
          currentCycle,
          userStakeId
        );
        toast.success(`Updated stake to ${newStakeAmount.toFixed(2)} coins!`);
        setStakeAmount('');
      } catch (error) {
        console.error('Error updating stake:', error);
        toast.error(error.message || 'Failed to update stake');
      } finally {
        setIsStaking(false);
      }
    } else {
      // First time staking - stake must not exceed available balance
      if (newStakeAmount > userData.balance) {
        toast.error(
          `Stake is larger than your balance. Your balance: ${userData.balance.toFixed(2)} coins`
        );
        return;
      }

      setIsStaking(true);

      try {
        await placeStake(roomId, userData, newStakeAmount, currentCycle);
        toast.success(`Staked ${newStakeAmount.toFixed(2)} coins!`);
        setStakeAmount('');
      } catch (error) {
        console.error('Error placing stake:', error);
        toast.error(error.message || 'Failed to place stake');
      } finally {
        setIsStaking(false);
      }
    }
  };

  // Create leaderboard with all players
  const leaderboard = allPlayers
    .map((playerPk) => {
      const stake = stakes.find((s) => s.playerPublicKey === playerPk);
      const participant = participantDetails[playerPk];
      return {
        publicKey: playerPk,
        email: stake?.playerEmail || (participant ? participant.email : null),
        name: participant ? participant.name : null,
        stakeAmount: stake?.stakeAmount || 0,
        stakedAt: stake?.stakedAt || null,
        isCurrentUser: playerPk === userData.publicKey,
      };
    })
    .sort((a, b) => b.stakeAmount - a.stakeAmount);

  const quickSetStake = (percentage) => {
    const max = parseFloat(
      userData.balance + (currentUserStake?.stakeAmount || 0)
    );
    const value = (max * (percentage / 100)).toFixed(2);
    setStakeAmount(value);
  };

  const formatAddress = (address) => {
    if (!address) return '';
    return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8 py-6">
      {/* Header with Countdown */}
      <div className="text-center relative py-6">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-indigo-500/10 blur-[120px] rounded-full -z-10"></div>
        <div className="inline-flex items-center gap-3 mb-4 px-4 py-1.5 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 text-sm font-medium">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-500"></span>
          </span>
          Live Competiiton
        </div>
        <h1 className="text-5xl font-bold text-white mb-6 tracking-tight drop-shadow-lg">
          STAKING PHASE
        </h1>
        <div className="flex justify-center mb-6 scale-125 origin-top">
          <GameCountdown
            endsAt={roomData.gameState?.stageEndsAt}
            onExpiry={handleStagingExpiry}
          />
        </div>
        <p className="text-white/40 text-lg max-w-xl mx-auto">
          Place your stake to compete for the Validator role. Highest stake at
          the end of the timer wins.
        </p>
      </div>

      <div className="flex justify-center items-start gap-8">
        {/* Staking Panel */}
        <div className="w-full max-w-2xl space-y-6">
          <div className="bg-[#0f111a]/60 backdrop-blur-xl rounded-2xl p-8 border border-white/5 relative overflow-hidden">
            {/* Dynamic background effect */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500/5 blur-[80px] rounded-full -z-10 pointer-events-none"></div>

            <div className="flex items-center justify-between mb-8">
              <h2 className="text-white font-semibold text-xl flex items-center gap-2">
                <span className="w-8 h-8 rounded-xl bg-purple-500/10 flex items-center justify-center text-purple-400 border border-purple-500/10">
                  <img src="/assets/images/a_logo.png" alt="logo" />
                </span>
                Place Your Stake
              </h2>
              <div className="text-right">
                <p className="text-white/40 text-xs font-medium uppercase tracking-wider mb-0.5">
                  Your Balance
                </p>
                <p className="text-white font-mono font-bold text-xl tracking-tight">
                  {userData.balance.toFixed(2)}{' '}
                </p>
              </div>
            </div>

            {/* Eligibility Check */}
            {!eligible && (
              <div className="mb-6 p-4 bg-red-500/10 rounded-xl border border-red-500/20 flex items-start gap-3">
                <svg
                  className="w-5 h-5 text-red-400 mt-0.5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                <p className="text-red-300 text-sm font-medium leading-relaxed">
                  {message}
                </p>
              </div>
            )}

            {/* Current Stake Status */}
            {hasStaked && currentUserStake && (
              <div className="mb-6 p-5 bg-gradient-to-r from-purple-500/10 to-indigo-500/10 rounded-xl border border-purple-500/20 flex items-center justify-between">
                <div>
                  <p className="text-purple-300 font-semibold text-sm mb-1">
                    Current Active Stake
                  </p>
                  <p className="text-purple-200/60 text-xs">
                    You are currently betting
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-white tracking-tight">
                    {currentUserStake.stakeAmount.toFixed(2)}
                  </p>
                </div>
              </div>
            )}

            {/* Stake Form */}
            {eligible && (
              <form onSubmit={handlePlaceStake} className="space-y-6">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-white/60 text-sm font-medium">
                      Amount to Stake
                    </label>
                    <span className="text-white/40 text-xs">
                      Max:{' '}
                      {(
                        userData.balance + (currentUserStake?.stakeAmount || 0)
                      ).toFixed(2)}
                    </span>
                  </div>
                  <div className="relative group">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30 font-medium">
                      $
                    </div>
                    <input
                      type="number"
                      placeholder="0.00"
                      value={stakeAmount}
                      onChange={(e) => setStakeAmount(e.target.value)}
                      step="any"
                      disabled={isStaking}
                      className="w-full bg-black/20 border border-white/10 rounded-xl pl-8 pr-4 py-4 text-white text-lg font-mono placeholder-white/20 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-all disabled:opacity-50"
                    />
                  </div>

                  {/* Quick Action Buttons */}
                  <div className="grid grid-cols-4 gap-2 mt-3">
                    {[25, 50, 75, 100].map((percent) => (
                      <button
                        key={percent}
                        type="button"
                        onClick={() => quickSetStake(percent)}
                        className="py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/5 hover:border-white/10 text-white/60 hover:text-white text-xs font-medium transition-colors"
                      >
                        {percent === 100 ? 'MAX' : `${percent}%`}
                      </button>
                    ))}
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isStaking || !stakeAmount}
                  className="w-full py-4 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 rounded-xl text-white font-bold text-lg shadow-lg shadow-purple-500/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-[0.99] border border-t-white/10"
                >
                  {isStaking ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Processing...
                    </span>
                  ) : hasStaked ? (
                    'Update Your Stake'
                  ) : (
                    'Confirm Stake'
                  )}
                </button>
              </form>
            )}
          </div>

          {/* Info Box */}
          <div className="bg-[#0f111a]/60 backdrop-blur-xl rounded-2xl p-6 border border-white/5 flex gap-4">
            <div className="mt-1">
              <div className="p-2 bg-cyan-500/10 rounded-lg text-cyan-400 border border-cyan-500/10">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="16" x2="12" y2="12"></line>
                  <line x1="12" y1="8" x2="12.01" y2="8"></line>
                </svg>
              </div>
            </div>
            <div>
              <h4 className="text-white font-medium text-sm mb-1">
                Staking Rules
              </h4>
              <p className="text-white/40 text-xs leading-relaxed">
                Higher stake wins the Validator role. In case of a tie, the
                earliest stake takes priority. All losing stakes are
                automatically refunded to your wallet.
              </p>
            </div>
          </div>
        </div>


      </div>
    </div>
  );
};
export default StakingStage;
